import { z } from 'zod';
import { globalPermissionSchema } from './permissions.js';

/** docs/03-datenmodell.md, users / organizations. */
export const userTypeSchema = z.enum(['internal', 'external']);
export type UserType = z.infer<typeof userTypeSchema>;

export const userStatusSchema = z.enum(['invited', 'active', 'deactivated']);
export type UserStatus = z.infer<typeof userStatusSchema>;

export const organizationStatusSchema = z.enum(['active', 'inactive']);
export type OrganizationStatus = z.infer<typeof organizationStatusSchema>;

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);

/** Trimmed and lower-cased so lookups match the citext column and Better Auth's normalization. */
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));

/** Sign-in methods offered on the login page (GET /api/v1/auth/methods). */
export const authMethodsResponseSchema = z.object({
  /** Microsoft Entra ID for internal users (AUTH-01); false while ENTRA_* is not configured. */
  microsoft: z.boolean(),
});
export type AuthMethodsResponse = z.infer<typeof authMethodsResponseSchema>;

export const userNameSchema = z.string().trim().min(1).max(200);

/** Six-digit TOTP code (RFC 6238, 30 s period). */
export const totpCodeSchema = z.string().regex(/^\d{6}$/);

/** Recovery (backup) code as issued by Better Auth, e.g. `AbCdE-12345`. */
export const backupCodeSchema = z.string().trim().min(1).max(64);

export const loginBodySchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type LoginBody = z.infer<typeof loginBodySchema>;

/**
 * Color scheme of the UI per account (NFR-06, E-34); default `light`, `system` follows
 * `prefers-color-scheme`.
 */
export const themePreferenceSchema = z.enum(['system', 'light', 'dark']);
export type ThemePreference = z.infer<typeof themePreferenceSchema>;

/** PUT /api/v1/me/preferences – own display preferences. */
export const userPreferencesSchema = z.object({ theme: themePreferenceSchema });
export type UserPreferences = z.infer<typeof userPreferencesSchema>;

/** GET /api/v1/auth/me – the signed-in user (AuthContext basics, AUTH-03..08). */
export const meResponseSchema = z.object({
  user: z.object({
    id: z.uuid(),
    email: z.string(),
    name: z.string(),
    userType: userTypeSchema,
    organizationId: z.uuid().nullable(),
    breakGlass: z.boolean(),
    theme: themePreferenceSchema,
    /** Own profile picture (ADM-07); null without one. */
    avatarUrl: z.string().nullable(),
  }),
  /** Effective global permissions (docs/04, section 4); for UI hints only, the API decides. */
  globalPermissions: z.array(globalPermissionSchema),
  /** TOTP is mandatory (AUTH-03, AUTH-08) but not yet set up: only 2FA setup is allowed. */
  twoFactorSetupRequired: z.boolean(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** Invitation tokens are 32 random bytes, base64url-encoded (43 characters). */
export const invitationTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

/** GET /api/v1/auth/invitations/:token */
export const invitationInfoResponseSchema = z.object({
  email: z.string(),
  name: z.string(),
  organizationName: z.string(),
  expiresAt: z.iso.datetime({ offset: true }),
});
export type InvitationInfoResponse = z.infer<typeof invitationInfoResponseSchema>;

/** POST /api/v1/auth/invitations/accept */
export const acceptInvitationBodySchema = z.object({
  token: invitationTokenSchema,
  password: passwordSchema,
});
export type AcceptInvitationBody = z.infer<typeof acceptInvitationBodySchema>;

/** Problem `type` URIs the frontend reacts to (RFC 9457). */
export const authProblemTypes = {
  /** Session is valid but mandatory TOTP is not set up yet. */
  twoFactorSetupRequired: 'urn:pm-tool:problem:two-factor-setup-required',
  /** Invitation token unknown, used or expired. */
  invitationInvalid: 'urn:pm-tool:problem:invitation-invalid',
} as const;

/** POST /api/v1/auth/sign-in/email – `twoFactorRedirect`: TOTP challenge follows. */
export const signInResponseSchema = z.object({
  twoFactorRedirect: z.boolean().optional(),
});

/** POST /api/v1/auth/sign-in/social – Microsoft authorization URL to send the browser to. */
export const socialSignInResponseSchema = z.object({
  url: z.url({ protocol: /^https?$/ }),
});

/** POST /api/v1/auth/two-factor/enable – QR code URI and one-time recovery codes. */
export const enableTwoFactorResponseSchema = z.object({
  totpURI: z.string().startsWith('otpauth://'),
  backupCodes: z.array(z.string()),
});

/** POST /api/v1/auth/two-factor/generate-backup-codes */
export const backupCodesResponseSchema = z.object({
  backupCodes: z.array(z.string()),
});
